// QuestionFecha - Pregunta tipo Fecha
// Sprint 3 - Mobile-first design

import React, { useState } from 'react';
import { Box, TextField, Chip, Typography } from '@mui/material';
import { PreguntaCompleta } from '../../../../types/formulario.types';
import { QuestionResponse } from '../../../../types/audit.types';

interface QuestionFechaProps {
  pregunta: PreguntaCompleta;
  respuesta?: QuestionResponse;
  onChange: (valor: any, comentario?: string) => void;
}

const QuestionFecha: React.FC<QuestionFechaProps> = ({
  pregunta,
  respuesta,
  onChange,
}) => {
  const valorActual = (respuesta as any)?.response_value as { fecha?: string } | undefined;
  const [comentario, setComentario] = useState((respuesta as any)?.comment || '');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fecha = event.target.value;
    onChange({ fecha }, comentario || undefined);
  };

  const handleComentarioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoComentario = event.target.value;
    setComentario(nuevoComentario);
    if (valorActual?.fecha) {
      onChange({ fecha: valorActual.fecha }, nuevoComentario || undefined);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' }, fontWeight: 500 }}>
            {(pregunta as any).text}
          </Box>
          {(pregunta as any).required && (
            <Chip label="Obligatoria" size="small" color="error" sx={{ height: 20, fontSize: '0.6875rem' }} />
          )}
        </Box>
        {(pregunta as any).description && (
          <Box sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontStyle: 'italic' }}>
            {(pregunta as any).description}
          </Box>
        )}
      </Box>

      {/* Campo de fecha */}
      <Box 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          bgcolor: 'action.hover', 
          borderRadius: 1,
          mb: pregunta.permite_comentario ? 2 : 0,
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            mb: 1.5, 
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
          }}
        >
          📅 Selecciona una fecha
        </Typography>

        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            type="date"
            value={valorActual?.fecha || ''}
            onChange={handleChange}
            variant="outlined"
            name={`pregunta-${pregunta.id}`}
            id={`pregunta-fecha-${pregunta.id}`}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                fontSize: { xs: '1rem', sm: '0.9375rem' },
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              },
              '& input': {
                py: { xs: 2, sm: 1.5 },
                fontSize: { xs: '1.0625rem', sm: '1rem' },
                fontWeight: 500,
                cursor: 'pointer',
                '&::-webkit-calendar-picker-indicator': {
                  cursor: 'pointer',
                },
                // Ocultar el placeholder nativo en mobile (iOS no lo soporta bien)
                '&::-webkit-datetime-edit-text': {
                  color: 'transparent',
                },
                '&::-webkit-datetime-edit-month-field': {
                  color: 'transparent',
                },
                '&::-webkit-datetime-edit-day-field': {
                  color: 'transparent',
                },
                '&::-webkit-datetime-edit-year-field': {
                  color: 'transparent',
                },
                // Cuando tiene valor, mostrar el texto normalmente
                '&:not(:placeholder-shown)': {
                  '&::-webkit-datetime-edit-text': {
                    color: 'inherit',
                  },
                  '&::-webkit-datetime-edit-month-field': {
                    color: 'inherit',
                  },
                  '&::-webkit-datetime-edit-day-field': {
                    color: 'inherit',
                  },
                  '&::-webkit-datetime-edit-year-field': {
                    color: 'inherit',
                  },
                },
              },
            }}
          />
          
          {/* Placeholder personalizado visible solo cuando está vacío */}
          {!valorActual?.fecha && (
            <Box
              sx={{
                position: 'absolute',
                left: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'text.secondary',
                fontSize: { xs: '1.0625rem', sm: '1rem' },
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              dd/mm/aaaa
            </Box>
          )}
        </Box>
      </Box>

      {/* Comentario */}
      {pregunta.permite_comentario && (
        <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mb: 1, 
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
            }}
          >
            Comentario Adicional
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Comentario (opcional)"
            value={comentario}
            onChange={handleComentarioChange}
            placeholder="Agrega un comentario..."
            variant="outlined"
            size="small"
            name={`comentario-${pregunta.id}`}
            id={`comentario-fecha-${pregunta.id}`}
            inputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            InputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuestionFecha;
